import {
  useState,
  useRef,
  TouchEvent,
  forwardRef,
  useImperativeHandle
} from "react";
import type * as React from "react";
import { Box, IconButton, useMediaQuery, useTheme } from "@mui/material";
import CandyButtonNext from "../../../../../assets/icons/candy-button-next.svg";
import CandyButtonNextDisabled from "../../../../../assets/icons/candy-button-next-disabled.svg";
import CandyButtonBack from "../../../../../assets/icons/candy-button-back.svg";
import CandyButtonBackDisabled from "../../../../../assets/icons/candy-button-back-disabled.svg";
import { COLORS } from "components/v3/Theme/colors";

// Define the slide interface
export interface Slide {
  id: string;
  content: React.ReactNode;
  wideModeSideContent?: React.ReactNode;
}

// Props interface for the ResultsSlider component
interface ResultsSliderProps {
  slides: Slide[];
  initialSlide?: number;
  showNavigation?: boolean;
  showDots?: boolean;
  onSlideChange?: (slideId: string) => void;
  onStepChange?: (step: number) => void;
}

export interface ResultsSliderRef {
  handleNext: () => void;
}

const ResultsSlider = forwardRef<ResultsSliderRef, ResultsSliderProps>(
  (
    {
      slides,
      initialSlide = 0,
      showNavigation = true,
      showDots = true,
      onSlideChange,
      onStepChange
    },
    ref
  ) => {
    const [activeStep, setActiveStep] = useState(initialSlide);
    const [touchStart, setTouchStart] = useState<number | null>(null);
    const [touchEnd, setTouchEnd] = useState<number | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const theme = useTheme();
    const screenWidthIsMdOrSmaller = useMediaQuery(
      theme.breakpoints.down("md")
    );

    // Minimum swipe distance (in px)
    const minSwipeDistance = 50;

    const handleNext = () => {
      const nextStep = Math.min(activeStep + 1, slides.length - 1);
      setActiveStep(nextStep);
      onSlideChange?.(slides[nextStep].id);
      onStepChange?.(nextStep);
    };

    useImperativeHandle(ref, () => ({
      handleNext
    }));

    const handleBack = () => {
      const prevStep = Math.max(activeStep - 1, 0);
      setActiveStep(prevStep);
      onSlideChange?.(slides[prevStep].id);
      onStepChange?.(prevStep);
    };

    const handleStepChange = (step: number) => {
      setActiveStep(step);
      onSlideChange?.(slides[step].id);
      onStepChange?.(step);
    };

    const onTouchStart = (e: TouchEvent) => {
      setTouchEnd(null);
      setTouchStart(e.targetTouches[0].clientX);
    };

    const onTouchMove = (e: TouchEvent) => {
      setTouchEnd(e.targetTouches[0].clientX);
    };

    const onTouchEnd = () => {
      if (!touchStart || !touchEnd) return;

      const distance = touchStart - touchEnd;
      const isLeftSwipe = distance > minSwipeDistance;
      const isRightSwipe = distance < -minSwipeDistance;

      if (isLeftSwipe && activeStep < slides.length - 1) {
        setActiveStep(activeStep + 1);
      }
      if (isRightSwipe && activeStep > 0) {
        setActiveStep(activeStep - 1);
      }
    };

    return (
      <Box
        sx={{ width: "100%", position: "relative" }}
        className={`${screenWidthIsMdOrSmaller ? "" : "limitHeight"}`}
      >
        <Box
          ref={containerRef}
          className={`${screenWidthIsMdOrSmaller ? "" : "limitHeight"}`}
          sx={{
            width: "100%",
            overflow: "hidden",
            position: "relative",
            "& > div": {
              display: "flex",
              transition: "transform 0.3s ease-out",
              transform: `translateX(-${(activeStep * 100) / slides.length}%)`,
              width: `${slides.length * 100}%`
            },
            "& > div > div": {
              width: `${100 / slides.length}%`,
              flexShrink: 0,
              boxSizing: "border-box"
            }
          }}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          <Box className={`${screenWidthIsMdOrSmaller ? "" : "limitHeight"}`}>
            {slides.map((slide) => (
              <Box
                key={slide.id}
                className={`${screenWidthIsMdOrSmaller ? "" : "limitHeight"}`}
              >
                {slide.content}
              </Box>
            ))}
          </Box>
        </Box>

        {showNavigation && (
          <Box
            sx={{
              position: "absolute",
              bottom: 16,
              right: 20,
              display: "flex",
              gap: 1.5,
              zIndex: 1
            }}
          >
            <IconButton
              onClick={handleBack}
              disabled={activeStep === 0}
              id="slider-back-button"
              sx={{
                padding: 0,
                "&:hover": { bgcolor: "transparent" },
                width: "30px",
                height: "30px"
              }}
            >
              <img
                src={
                  activeStep === 0 ? CandyButtonBackDisabled : CandyButtonBack
                }
                alt="Previous slide"
                style={{ width: "50px", height: "50px" }}
              />
            </IconButton>
            <IconButton
              onClick={handleNext}
              id="slider-next-button"
              disabled={activeStep === slides.length - 1}
              sx={{
                padding: 0,
                "&:hover": { bgcolor: "transparent" },
                width: "30px",
                height: "30px"
              }}
            >
              <img
                src={
                  activeStep === slides.length - 1
                    ? CandyButtonNextDisabled
                    : CandyButtonNext
                }
                alt="Next slide"
                style={{ width: "50px", height: "50px" }}
              />
            </IconButton>
          </Box>
        )}

        {showDots && (
          <Box
            sx={{
              position: "absolute",
              bottom: 20,
              left: 20,
              display: "flex",
              gap: 1,
              zIndex: 1
            }}
          >
            {slides.map((_, index) => (
              <Box
                key={index}
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  bgcolor:
                    activeStep === index
                      ? COLORS.ACCENT2
                      : COLORS.ACCENT2_LIGHTER,
                  cursor: "pointer"
                }}
                onClick={() => handleStepChange(index)}
              />
            ))}
          </Box>
        )}
      </Box>
    );
  }
);

export default ResultsSlider;
